import type { CSSProperties } from 'react'
import type { TextHighlight } from '../data/v1'

/**
 * `text` styles the editable text element, `wrapper` the block around it. The two are
 * returned together because they cancel out: the inline box's horizontal padding shifts the
 * text inwards, and the wrapper's matching negative margin pulls it back, so the highlight
 * bleeds past the text instead of moving it.
 */
export type TextHighlightStyles = {
	text: CSSProperties
	wrapper: CSSProperties
}

const NO_HIGHLIGHT: TextHighlightStyles = { text: {}, wrapper: {} }

export function textHighlightStyle(highlight: TextHighlight, color: string | undefined): TextHighlightStyles {
	if (highlight.style === 'none' || !color) {
		return NO_HIGHLIGHT
	}

	const text: CSSProperties = {
		display: 'inline',
		boxDecorationBreak: 'clone',
		WebkitBoxDecorationBreak: 'clone',
		paddingInline: highlight.paddingX,
		paddingBlock: highlight.paddingY,
	}
	const wrapper: CSSProperties = { marginInline: -highlight.paddingX }
	const paint = withOpacity(color, highlight.opacity)

	switch (highlight.style) {
		case 'marker':
			return {
				text: { ...text, backgroundColor: paint, borderRadius: highlight.borderRadius },
				wrapper,
			}
		case 'underline':
			return {
				text: {
					...text,
					backgroundImage: `linear-gradient(${paint}, ${paint})`,
					backgroundSize: `100% ${highlight.thickness}%`,
					backgroundPosition: `0 calc(100% - ${highlight.offsetY}px)`,
					backgroundRepeat: 'no-repeat',
				},
				wrapper,
			}
		case 'brush':
			return {
				text: {
					...text,
					backgroundImage: brushImage(color, highlight.opacity),
					backgroundSize: '100% 100%',
					backgroundPosition: '0 0',
					backgroundRepeat: 'no-repeat',
				},
				wrapper,
			}
	}
}

function withOpacity(color: string, opacity: number): string {
	return opacity >= 100 ? color : `color-mix(in srgb, ${color} ${opacity}%, transparent)`
}

/** A marker stroke with uneven edges, stretched to fill each line fragment. */
function brushImage(color: string, opacity: number): string {
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 40" preserveAspectRatio="none">`
		+ `<path fill="${color}" fill-opacity="${opacity / 100}" `
		+ `d="M3 7C42 2 92 5 197 2c2 11 1 25-1 36-47 3-136-1-192 1C1 29 1 17 3 7Z"/>`
		+ `</svg>`

	return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}
